import * as Api from '@ViewModels';
import { css, StyleDeclarationValue } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { useLocation } from 'react-router';
import { toast } from 'sonner';
import {
	IContentCalendarSelectedSuggestion,
	IImpersonationContextComponentProps,
	ILocationState,
	IModalContext,
	IMultiCampaignSchedulerRequest,
	IMultiCampaignSchedulerResult,
	ImpersonationContextKey,
	ModalChildComponentContextKey,
} from '../../../../../../models';
import { CampaignType } from '../../../../../../models/AdminModels';
import { useEnvironment, useErrorMessages, useUserSession } from '../../../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../../../models/Logging';
import { accountHasComplianceEnabled, getUniqueIdForSuggestion } from '../../../../../../models/UiUtils';
import { hasAnyBulkAiEligibleSuggestion } from '../../../../../../models/utils/contentCalendarBulkAiUtils';
import { useAccountProfileQuery, useTemplateQueries } from '../../../../../../queries';
import { hasContentPreferences } from '../../../../../../utils/hasContentPreferences';
import { Button } from '../../../../../components/Button';
import { useCampaignCalendarSuggestionsContext } from '../../../../../components/campaigns/CampaignCalendar/context';
import { ComplianceApprovalPrompt } from '../../../../../components/campaigns/ComplianceApprovalPrompt';
import { AiButton } from '../../../../../components-v2/AiButton';
import { baseStyleSheet } from '../../../../../styles/styles';
import { useHandleAiGeneration } from '../../../hooks/handleAiGeneration';
import { WarningBanner, WarningBannerMessage } from '../../../presentation';
import { MultiSchedulerAiModal } from '../../aiModal';
import { MultiCampaignSchedulerLayout } from '../../MultiCampaignSchedulerLayout';
import { MultiBlogTemplateItem } from '../MultiBlogTemplateItem';
import { useScheduleBlogSuggestions } from './hooks';
import { styleSheet } from './styles';
import { useAutoGenerateBlogPosts } from './useAutoGenerateBlogPosts';

interface IProps extends IImpersonationContextComponentProps, IModalContext {
	styles?: StyleDeclarationValue[];
}

type Author = {
	sendFrom: Api.ISendEmailFrom;
	sendFromUserId: string | null;
};

export const MultiBlogScheduler = inject(
	ImpersonationContextKey,
	ModalChildComponentContextKey
)(
	observer(function MultiBlogScheduler({ styles = [], impersonationContext, parentModal }: IProps) {
		const location = useLocation<ILocationState<any, IMultiCampaignSchedulerRequest>>();
		const isSequenceStep = location.state?.model?.isSequenceStep;
		const userSession = useUserSession();
		const errorMessages = useErrorMessages();
		const accountProfileQuery = useAccountProfileQuery({ impersonationContext });
		const { logApiError } = useEventLogging('MultiBlogScheduler');
		const requiresCompliance = accountHasComplianceEnabled(userSession, impersonationContext);

		const [generatedContent, setGeneratedContent] = React.useState<Record<string, Api.IRawRichTextContentState>>({});

		const {
			selectedSuggestions,
			deselectSuggestion,
			updateStartDateForSuggestion,
			getStartDateForSuggestion,
			onScheduleSuccess,
		} = useCampaignCalendarSuggestionsContext();

		const isAdminView = useEnvironment()?.appType === 'admin';

		const draftAiContent = React.useMemo(() => {
			const map: Record<string, { subject: string; content: Api.IRawRichTextContentState }> = {};
			selectedSuggestions.forEach(suggestion => {
				const card = suggestion._cachedTemplateCard;
				if (card?.content) {
					map[getUniqueIdForSuggestion(suggestion)] = {
						subject: card.name,
						content: card.content,
					};
				}
			});
			return map;
		}, [selectedSuggestions]);

		const { pendingItems, queueItemForAiGeneration, isGenerating, processQueue, suggestionsWithErrors } =
			useHandleAiGeneration({
				contentType: 'blogTemplate',
				impersonationContext,
				customContent: draftAiContent,
				onAiGenerationComplete: (suggestion: IContentCalendarSelectedSuggestion, toolCall: Api.BlogToolCall) => {
					const key = getUniqueIdForSuggestion(suggestion);
					setGeneratedContent(prev => ({ ...prev, [key]: toolCall.content }));
				},
			});
		const [showingOriginalContent, setShowingOriginalContent] = React.useState<string[]>([]);

		const [author, setAuthor] = React.useState<Author>(() => {
			let sendFrom: Api.ISendEmailFrom = Api.SendEmailFrom.CurrentUser;
			let sendFromUserId: string = null;
			if (impersonationContext?.isValid) {
				sendFrom = Api.SendEmailFrom.SelectedUser;
				sendFromUserId = impersonationContext.user?.id || null;
			} else {
				sendFromUserId = userSession.user.id;
			}

			return {
				sendFrom,
				sendFromUserId,
			};
		});

		const scheduledPostsRef = React.useRef<Map<string, Api.IBlogPost>>(new Map());
		const suggestionPostErrorsRef = React.useRef<
			Set<[IContentCalendarSelectedSuggestion, Api.IOperationResultNoValue]>
		>(new Set());

		// Build a map of cached template card data from suggestions (populated during drag-drop
		// for blog drafts so the multi-scheduler can render them without re-fetching by template id).
		const cachedTemplateCards = React.useMemo(() => {
			const map: Record<string, Api.ITemplate> = {};
			selectedSuggestions.forEach(s => {
				const card = s._cachedTemplateCard;
				if (card) {
					map[s.templateReference.templateId] = {
						id: card.id,
						name: card.name,
						title: card.name,
						content: card.content,
						scope: card.scope,
						attachments: card.attachments,
						templateType: Api.TemplateType.Blog,
					} as Api.ITemplate;
				}
			});
			return map;
		}, [selectedSuggestions]);

		// Build a queryFn that tries the standard endpoint first, with fallbacks:
		// - Drafts: use cached card data from drag-drop (works for drafts created by other users)
		// - Admin templates: fall back to the admin industry template endpoint
		const impersonationJs = impersonationContext?.toJs();
		const blogTemplateQueryFn = React.useCallback(
			(templateId: string, session: Api.UserSessionContext): Promise<Api.ITemplate> => {
				const standardFetch = session.webServiceHelper.callAsync<Api.ITemplate>(
					Api.ImpersonationBroker.composeApiUrl({
						impersonationContext: impersonationJs,
						urlPath: `template/${encodeURIComponent(templateId)}`,
						queryParams: { expand: 'LastUsedByDate' },
					}),
					'GET'
				);

				return standardFetch.catch(error => {
					const cached = cachedTemplateCards[templateId];
					if (cached) {
						return cached;
					}

					if (isAdminView) {
						return session.webServiceHelper
							.callAsync<{ template: Api.IBlogTemplate }>(
								Api.ImpersonationBroker.composeApiUrl({
									impersonationContext: impersonationJs,
									urlPath: `admin/template/industry/blogCategories/templates/${encodeURIComponent(templateId)}`,
								}),
								'GET'
							)
							.then(result => result.template as Api.ITemplate);
					}

					throw error;
				});
			},
			[impersonationJs, isAdminView, cachedTemplateCards]
		);

		const templateQueries = useTemplateQueries({
			impersonationContext: impersonationJs,
			templateIds: selectedSuggestions.map(t => t.templateReference.templateId),
			refetchOnWindowFocus: false,
			queryKeyProvider: (_, index) => {
				const suggestion = selectedSuggestions[index];
				return getUniqueIdForSuggestion(suggestion);
			},
			queryFnOverride: blogTemplateQueryFn,
		});

		const {
			generatedPosts: autoGeneratedPosts,
			generatingIds: autoGeneratingIds,
			errorIds: autoGenerationErrorIds,
			retryGeneration: retryAutoGeneration,
		} = useAutoGenerateBlogPosts({
			templateQueries,
			selectedSuggestions,
			impersonationContext,
		});

		const scheduleMutation = useScheduleBlogSuggestions({
			onError: error => {
				logApiError('ScheduleBlogSuggestions-Error', error);
				errorMessages.pushApiError(error);
			},
			onSuccess: (results, params) => {
				results.forEach((result, i) => {
					const suggestion = params.suggestions[i];
					if (result.status === 'fulfilled') {
						const blogPost = result.value;
						scheduledPostsRef.current.set(getUniqueIdForSuggestion(suggestion), blogPost);
					} else {
						const error = Api.asApiError(result.reason);
						const matchingSuggestion = selectedSuggestions.find(
							s => getUniqueIdForSuggestion(suggestion) === getUniqueIdForSuggestion(s)
						);
						suggestionPostErrorsRef.current.add([matchingSuggestion, error]);
					}
				});

				if (scheduledPostsRef.current.size === selectedSuggestions.length) {
					const scheduled = Array.from(scheduledPostsRef.current?.values()) || [];
					const scheduleResult: IMultiCampaignSchedulerResult = {
						scheduledSuggestions: selectedSuggestions,
						withCompliance: params.sendWithCompliance,
					};

					toast.success(`${scheduled.length} blog posts scheduled.`);

					const event = onScheduleSuccess(scheduleResult);
					if (!event.defaultPrevented && !isSequenceStep) {
						parentModal.onRequestClose();
					}
				}
			},
		});

		const scheduleSuggestions = (sendWithCompliance = false, sendWithComplianceEmail?: string) => {
			suggestionPostErrorsRef.current?.clear();
			const unscheduledSuggestions = scheduledPostsRef.current
				? selectedSuggestions.filter(suggestion => {
						return !scheduledPostsRef.current.has(getUniqueIdForSuggestion(suggestion));
					})
				: selectedSuggestions;
			if (unscheduledSuggestions?.length) {
				scheduleMutation.mutate({
					impersonationContext,
					suggestions: unscheduledSuggestions.map(suggestion => {
						return {
							...suggestion,
							schedule: {
								startDate: getStartDateForSuggestion(suggestion).toISOString(),
							},
						} as IContentCalendarSelectedSuggestion;
					}),
					generatedContent,
					autoGeneratedPosts,
					sendWithCompliance,
					sendWithComplianceEmail,
					forUserId: author.sendFromUserId,
					templateMap: Object.fromEntries(
						templateQueries.flatMap(q => {
							if (!q.data) {
								return [];
							}

							return [
								[
									q.data.id,
									{
										template: q.data,
										mainImage: q.data.attachments?.[0],
									},
								],
							];
						})
					),
				});
			}
		};

		const suggestionsWithoutDatesCount = selectedSuggestions.filter(s => !getStartDateForSuggestion(s)).length;
		const hasUnresolvedTemplateQueries = templateQueries.some(q => q.isPending || q.isError || !q.data);

		const isAutoGenerating = autoGeneratingIds.length > 0;
		const canSchedule =
			suggestionsWithoutDatesCount === 0 &&
			!scheduleMutation.isPending &&
			!isGenerating &&
			!isAutoGenerating &&
			!hasUnresolvedTemplateQueries &&
			(impersonationContext?.isValid ? impersonationContext.user?.id : true);

		const renderSideBar = () => {
			if (requiresCompliance) {
				return (
					<ComplianceApprovalPrompt
						disabled={!canSchedule}
						isLoading={scheduleMutation.isPending}
						campaignType={CampaignType.Blog}
						onScheduleCompliance={(e: React.MouseEvent<HTMLElement>, sendWithComplianceEmail: string) => {
							e.preventDefault();
							scheduleSuggestions(true, sendWithComplianceEmail);
						}}
						onScheduleWithoutCompliance={() => scheduleSuggestions()}
					/>
				);
			}

			return (
				<div>
					<Button
						disabled={!canSchedule || scheduleMutation.isPending}
						className={css(baseStyleSheet.horizontalStack, baseStyleSheet.fullWidth)}
						onClick={() => scheduleSuggestions()}
						label='Schedule'
						isLoading={scheduleMutation.isPending}
					/>
				</div>
			);
		};

		return (
			<div className={css(styleSheet.container, ...styles)}>
				{suggestionsWithoutDatesCount > 0 ? (
					<WarningBanner>
						<>
							<WarningBannerMessage>
								{`You have `}
								<strong>{suggestionsWithoutDatesCount}</strong>
								{` without a send date. Please select a date/time to continue scheduling these posts.`}
							</WarningBannerMessage>
						</>
					</WarningBanner>
				) : null}
				<MultiCampaignSchedulerLayout
					{...author}
					hideContactOwnerOption
					onSendFromChange={(option, user) => {
						impersonationContext?.updateUser(user?.id ? { id: user.id } : null);
						setAuthor({ sendFrom: option.sender.mode, sendFromUserId: user?.id });
					}}
					sideBar={renderSideBar()}
					title='Schedule Blog Posts'
					userSelectLabel='Author'
					styles={[styleSheet.schedulerContainer]}
				>
					{isAdminView && hasContentPreferences(accountProfileQuery.data) && (
						<div className={css(styleSheet.customizeAllContentContainer)}>
							<AiButton
								title='Make Content Unique with AI'
								disabled={!hasAnyBulkAiEligibleSuggestion(selectedSuggestions)}
								onClick={() => {
									selectedSuggestions.forEach(suggestion =>
										queueItemForAiGeneration(suggestion, { forBulkAction: true })
									);
									processQueue();
								}}
								isLoading={isGenerating}
							/>
						</div>
					)}

					<div className={css(styleSheet.content)}>
						{templateQueries.map((templateQuery, i) => {
							const suggestion = selectedSuggestions[i];
							const id = getUniqueIdForSuggestion(suggestion);

							const aiGenerationFailed = suggestionsWithErrors.some(
								(s: IContentCalendarSelectedSuggestion) => id === getUniqueIdForSuggestion(s)
							);
							const autoGenerationFailed = autoGenerationErrorIds.includes(id);
							const shouldShowOriginalContent = showingOriginalContent.includes(id);
							const template = templateQuery.data;
							const isAutoGeneratingItem = autoGeneratingIds.includes(id);
							const isAiGeneratingItem = pendingItems.some(item => item.templateReference.templateId === template?.id);

							return (
								<MultiBlogTemplateItem
									onUpdateDate={newDate => updateStartDateForSuggestion(suggestion, newDate)}
									scheduled={scheduledPostsRef.current?.has(getUniqueIdForSuggestion(suggestion))}
									error={Array.from(suggestionPostErrorsRef.current || []).find(([s]) => s === suggestion)?.[1]}
									suggestion={suggestion}
									key={getUniqueIdForSuggestion(suggestion)}
									query={templateQuery}
									scheduleDate={getStartDateForSuggestion(suggestion)}
									onRemove={
										templateQueries.length > 1 && !scheduleMutation.isPending
											? () => deselectSuggestion(suggestion)
											: undefined
									}
									isGeneratingContent={isAutoGeneratingItem || isAiGeneratingItem}
									generationFailed={aiGenerationFailed || autoGenerationFailed}
									onGenerateContent={() => {
										if (autoGenerationFailed) {
											retryAutoGeneration(suggestion);
										} else {
											queueItemForAiGeneration(suggestion);
											processQueue();
										}
									}}
									showOriginalContent={shouldShowOriginalContent}
									onShowOriginalContent={
										generatedContent[id]
											? () =>
													setShowingOriginalContent(prev => {
														if (prev.includes(id)) {
															return prev.filter(showingId => showingId !== id);
														}
														return [...prev, id];
													})
											: undefined
									}
									generatedContent={generatedContent[id]}
									autoGeneratedContent={autoGeneratedPosts[id]?.content}
									{...author}
								/>
							);
						})}
					</div>

					<MultiSchedulerAiModal
						hasEligibleBulkAiSuggestion={hasAnyBulkAiEligibleSuggestion(selectedSuggestions)}
						onGenerateContentClicked={() => {
							selectedSuggestions.forEach(suggestion => queueItemForAiGeneration(suggestion, { forBulkAction: true }));
							processQueue();
						}}
						impersonationContext={impersonationContext}
					/>
				</MultiCampaignSchedulerLayout>
			</div>
		);
	})
);
